import React from "react";
import {
    Col,
    Layout,
    Radio,
    Row,
    Switch,
    Table,
    Tooltip,
    Typography,
} from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import { RadioChangeEvent } from "antd/es/radio/interface";
import {
    Cell,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
} from "recharts";

import { BitmakerAPIClient } from "../../services/BitmakerApp";
import {
    MaintenanceEpicRow,
    MaintenanceEpicRowFromJSON,
    ProcessEfficiencyRow,
    ProcessEfficiencyRowFromJSON,
} from "../../bitmaker/schemas/ProfessionalServicesKPIs";
import { fromDateToStringWithMonthInLetters } from "../../utils";
import { REDMINE_URL } from "../../constants";

const { Content } = Layout;
const { Title } = Typography;

const stageColors: any = {
    "Analysis": "rgba(133, 32, 12, 0.3)",
    "Development": "rgba(180, 95, 6, 0.3)",
    "Quality Assurance": "rgba(56, 118, 29, 0.3)",
    "Data Sample Approval": "rgba(11, 83, 148, 0.3)",
    // "Full Crawl Approval": "rgba(53, 28, 117, 0.3)",
    "Project Handover": "rgba(67, 67, 67, 0.3)",
    "Support": "rgba(19, 79, 92, 0.3)"
};

interface Props {
}

interface State {
    processEfficiencyRows: Array<ProcessEfficiencyRow>;
    metricsPerDeveloperRows: Array<{
        user_login: string;
        contract_count: number;
        process_efficiency: number;
    }>;
    processEfficiencyOverTimeRows: Array<{
        period: string;
        process_efficiency: number;
    }>;
    contractCountPerStageRows: Array<{
        stage_name: string;
        contract_count: number;
    }>;
    workloadRows: Array<{
        sprint_id: number;
        sprint_name: string;
        new_contracts: number;
        closed_contracts: number;
        backlog_size: number;
        issues: number;
        recurring_issues: number;
        total_issues: number;
        spillover_issues: number;
        is_current: boolean;
    }>;
    maintenanceEpicsRows: Array<MaintenanceEpicRow>;
    currentPage: number;
    pageSize: number;
    totalItems: number;
    processEfficiencyLoading: boolean;
    metricsPerDeveloperLoading: boolean;
    processEfficiencyOverTimeLoading: boolean;
    contractCountPerStageLoading: boolean;
    workloadLoading: boolean;
    maintenanceEpicsLoading: boolean;
    developersList: Array<{ id: number; login: string }>;
    developmentStagesList: Array<{ id: number; name: string }>;
    contractCountTableSelectedFilter: string;
    selectedLeadTimeUnit: string;
}

export class ProfessionalServicesKPIs extends React.PureComponent<Props, State> {
    processEfficiencyColumns: ColumnsType<ProcessEfficiencyRow> = [
        {
            key: "id",
            title: "Epic ID",
            dataIndex: "id",
            align: "center",
            width: "4%",
            render: (_, record) => (
                <a
                    href={`${REDMINE_URL}/issues/${record.id}`}
                    target="_blank"
                    rel="noreferrer"
                >
                    {record.id}
                </a>
            ),
        },
        {
            key: "project",
            title: "Project",
            dataIndex: "project",
            width: "12%",
            render: (_, record) => (
                <a
                    href={`${REDMINE_URL}/projects/${record.project_id}`}
                    target="_blank"
                    rel="noreferrer"
                >
                    {record.project}
                </a>
            ),
        },
        {
            key: "assignee",
            title: "Developer",
            dataIndex: "assignee",
            width: "12%",
            onFilter: (value, record) => record.assignee_id === value,
            render: (_, record) => (
                <span>{record.assignee ? record.assignee : "-"}</span>
            ),
        },
        {
            key: "subject",
            title: "Subject",
            dataIndex: "subject",
            width: "24%",
            render: (_, record) => (
                <a
                    href={`${REDMINE_URL}/issues/${record.id}`}
                    target="_blank"
                    rel="noreferrer"
                >
                    {record.subject}
                </a>
            ),
        },
        {
            key: "start_date",
            title: "Start date",
            dataIndex: "start_date",
            align: "right",
            width: "11%",
            render: (_, record) => (
                <span>{fromDateToStringWithMonthInLetters(record.start_date)}</span>
            ),
        },
        {
            key: "close_date",
            title: "Closure date",
            dataIndex: "close_date",
            align: "right",
            width: "11%",
            filters: [
                { text: "Closed", value: "closed" },
                { text: "Open", value: "open" },
            ],
            filterMultiple: false,
            onFilter: (value, record) => {
                if (value === "closed") {
                    return !!record.closed_on;
                }
                if (value === "open") {
                    return !record.closed_on;
                }
                return true;
            },
            render: (_, record) => (
                <span>
                    {record.close_date
                        ? fromDateToStringWithMonthInLetters(record.close_date)
                        : "-"}
                </span>
            ),
        },
        {
            key: "lead_time",
            dataIndex: "days_difference",
            align: "right",
            render: (value) => {
                const selectedUnit = this.state.selectedLeadTimeUnit;
                return selectedUnit === "hours" ? `${value * 8}` : `${value}`;
            },
        },
        {
            key: "estimated_time",
            title: (
                <Tooltip
                    title="Estimated hours for the ticket."
                    placement="leftBottom"
                >
                    Estimated effort
                    <br />
                    <i style={{ fontSize: "0.8em" }}>
                        Redmine estimated time (hours)
                    </i>
                </Tooltip>
            ),
            dataIndex: "estimated_time",
            align: "right",
            render: (value) => <span>{value ? value.toFixed(2) : "-"}</span>,
        },
        {
            key: "total_spent_time",
            title: (
                <Tooltip
                    title="Total hours logged on the ticket and all its sub-tasks."
                    placement="leftBottom"
                >
                    Cycle time (Effort)
                    <br />
                    <i style={{ fontSize: "0.8em" }}>Redmine spent time (hours)</i>
                </Tooltip>
            ),
            dataIndex: "total_spent_time",
            align: "right",
            render: (value) => <span>{value.toFixed(2)}</span>,
        },
        {
            key: "effort_deviation",
            title: (
                <Tooltip
                    title={`
                                Difference between the spent time and the estimated time. Calculated as (Cycle time - Estimated effort).
                                Positive values indicate time spent exceeds the estimate, while negative values mean the spent time is within the estimate.
                            `}
                    placement="leftBottom"
                >
                    Effort deviation
                    <br />
                    <i style={{ fontSize: "0.8em" }}>
                        Spent - Estimated time (hours)
                    </i>
                </Tooltip>
            ),
            dataIndex: "effort_deviation",
            align: "right",
            render: (value) => <span>{value.toFixed(2)}</span>,
        },
        {
            key: "deviation_rate",
            title: (
                <Tooltip
                    title={`
                                Percentage of deviation from the estimated time.
                                If Effort deviation is negative, the rate is set to 0.
                                Otherwise, it's calculated as (Effort deviation / Estimated effort) * 100.
                                Cells are colored red if the deviation exceeds 50%.
                            `}
                    placement="leftBottom"
                >
                    Deviation rate
                    <br />
                    <i style={{ fontSize: "0.8em" }}>Goal: under 20%</i>
                </Tooltip>
            ),
            dataIndex: "deviation_rate",
            align: "right",
            render: (value) => {
                const className = value > 50 ? "red-cell" : "";
                return {
                    props: {
                        className,
                    },
                    children: value != null ? `${value.toFixed(2)}%` : "-",
                };
            },
        },
        {
            key: "process_efficiency",
            title: (
                <Tooltip
                    title="Percentage of time spent on the task relative to the total time available. Calculated as (Cycle Time / Lead Time) * 100."
                    placement="leftBottom"
                >
                    Process efficiency
                    <br />
                    <i style={{ fontSize: "0.8em" }}>Goal: over 20%</i>
                </Tooltip>
            ),
            dataIndex: "process_efficiency",
            align: "right",
            render: (value) => <span>{value.toFixed(2)}%</span>,
        },
        {
            key: "development_stage",
            title: "Development Stage",
            dataIndex: "development_stage",
            width: "11%",
            onFilter: (value, record) => record.development_stage === value,
            render: (_, record) => (
                <span>
                    {record.development_stage ? record.development_stage : "-"}
                </span>
            ),
        },
    ];

    metricsPerDeveloperColumns: ColumnsType<{
        user_login: string;
        contract_count: number;
        process_efficiency: number;
    }> = [
            {
                key: "user_login",
                title: "User",
                dataIndex: "user_login",
                align: "center",
                width: "40%",
            },
            {
                key: "contract_count",
                title: "Contract Count",
                dataIndex: "contract_count",
                align: "center",
                width: "30%",
            },
            {
                key: "process_efficiency",
                title: "Process efficiency",
                dataIndex: "process_efficiency",
                align: "center",
                width: "30%",
                render: (value) => <span>{value.toFixed(2)}%</span>,
            },
        ];

    workloadColumns: ColumnsType<{
        sprint_id: number;
        sprint_name: string;
        new_contracts: number;
        closed_contracts: number;
        backlog_size: number;
        total_issues: number;
        spillover_issues: number;
        is_current: boolean;
    }> = [
            {
                key: "sprint_name",
                title: "Sprint",
                dataIndex: "sprint_name",
                align: "center",
                width: "30%",
            },
            {
                key: "new_contracts",
                title: (
                    <Tooltip
                        title="The number of contracts whose start date falls within the sprint's start and end dates."
                        placement="leftBottom"
                    >
                        New Contracts
                        <br />
                        <i style={{ fontSize: "0.7em" }}>
                            Contracts started during the sprint
                        </i>
                    </Tooltip>
                ),
                dataIndex: "new_contracts",
                align: "center",
                width: "10%",
            },
            {
                key: "closed_contracts",
                title: (
                    <Tooltip
                        title="The number of contracts whose closed on value falls within the sprint's start and end dates."
                        placement="leftBottom"
                    >
                        Closed Contracts
                        <br />
                        <i style={{ fontSize: "0.7em" }}>
                            Contracts closed during the sprint
                        </i>
                    </Tooltip>
                ),
                dataIndex: "closed_contracts",
                align: "center",
                width: "10%",
            },
            {
                key: "backlog_size",
                title: (
                    <Tooltip
                        title="Calculated as the previous sprint's backlog size plus the difference between new and closed contracts."
                        placement="leftBottom"
                    >
                        Backlog Size
                        <br />
                        <i style={{ fontSize: "0.7em" }}>
                            Previous backlog size + (new contracts - closed contracts)
                        </i>
                    </Tooltip>
                ),
                dataIndex: "backlog_size",
                align: "center",
                width: "10%",
            },
            {
                key: "issues",
                title: (
                    <Tooltip
                        title={`
                            The number of issues assigned to the sprint that are neither recurring nor spillover issues
                            and belong to Professional Services (PS) or Business Intelligence (BI).
                        `}
                        placement="leftBottom"
                    >
                        Issues
                        <br />
                        <i style={{ fontSize: "0.7em" }}>
                            Non-recurring, non-spillover PS/BI sprint issues
                        </i>
                    </Tooltip>
                ),
                dataIndex: "issues",
                align: "center",
                width: "10%",
            },
            {
                key: "recurring_issues",
                title: (
                    <Tooltip
                        title="The number of recurring issues (recurring or external review) assigned to the sprint that belong to PS (Professional Services) or BI (Business Intelligence)."
                        placement="leftBottom"
                    >
                        Recurring Issues
                        <br />
                        <i style={{ fontSize: "0.7em" }}>
                            Recurring PS/BI sprint issues
                        </i>
                    </Tooltip>
                ),
                dataIndex: "recurring_issues",
                align: "center",
                width: "10%",
            },
            {
                key: "spillover_issues",
                title: (
                    <Tooltip
                        title={`
                            The number of spillover issues from the previous sprint that belong to Professional Services (PS) or Business Intelligence (BI)
                            and have been carried over to the current sprint.
                        `}
                        placement="leftBottom"
                    >
                        Spillover Issues
                        <br />
                        <i style={{ fontSize: "0.7em" }}>
                            PS/BI issues carried from previous sprint
                        </i>
                    </Tooltip>
                ),
                dataIndex: "spillover_issues",
                align: "center",
                width: "10%",
            },
            {
                key: "total_issues",
                title: (
                    <Tooltip
                        title="The total number of issues in the sprint, calculated as the sum of issues, recurring issues, and spillover issues."
                        placement="leftBottom"
                    >
                        Total Issues
                        <br />
                        <i style={{ fontSize: "0.7em" }}>
                            Issues + recurring + spillover
                        </i>
                    </Tooltip>
                ),
                dataIndex: "total_issues",
                align: "center",
                width: "10%",
            },
        ];

    maintenanceEpicsColumns: ColumnsType<MaintenanceEpicRow> = [
        {
            key: "project",
            title: "Project",
            dataIndex: "project",
            width: "40%",
        },
        {
            key: "maintenance_hours",
            title: "Monthly maintenance hours limit",
            dataIndex: "maintenance_hours",
            width: "20%",
            align: "right",
        },
        {
            key: "start_of_maintenance",
            title: "Start of maintenance date",
            dataIndex: "start_of_maintenance",
            width: "20%",
            align: "right",
            render: (_, record) => (
                <span>
                    {record.start_of_maintenance
                        ? fromDateToStringWithMonthInLetters(
                            record.start_of_maintenance,
                        )
                        : "-"}
                </span>
            ),
        },
        {
            key: "end_of_maintenance",
            title: "End of maintenance date",
            dataIndex: "end_of_maintenance",
            width: "20%",
            align: "right",
            render: (_, record) => (
                <span>
                    {record.end_of_maintenance
                        ? fromDateToStringWithMonthInLetters(
                            record.end_of_maintenance,
                        )
                        : "-"}
                </span>
            ),
        },
    ];

    constructor(props: Props) {
        super(props);
        this.state = {
            processEfficiencyRows: [],
            processEfficiencyOverTimeRows: [],
            currentPage: 1,
            pageSize: 10,
            totalItems: 0,
            processEfficiencyLoading: false,
            metricsPerDeveloperLoading: false,
            processEfficiencyOverTimeLoading: false,
            contractCountPerStageLoading: false,
            workloadLoading: false,
            maintenanceEpicsLoading: false,
            metricsPerDeveloperRows: [],
            contractCountPerStageRows: [],
            workloadRows: [],
            developersList: [],
            developmentStagesList: [],
            maintenanceEpicsRows: [],
            contractCountTableSelectedFilter: "all",
            selectedLeadTimeUnit: "hours",
        };
    };

    public componentDidMount() {
        this.fetchProcessEfficiencyData();
        this.fetchDevelopersListData();
        this.fetchDevelopmentStagesListData();
        this.fetchMetricsPerDeveloperData();
        this.fetchProcessEfficiencyOverTimeData();
        this.fetchContractCountPerStageData();
        this.fetchWorkloadData();
        this.fetchMaintenanceEpicsData();
    };

    fetchProcessEfficiencyData = (
        filters: {
            closed_on: string | null;
            assignee: number[] | null;
            development_stage: string[] | null;
        } = {
                closed_on: null,
                assignee: null,
                development_stage: null,
            },
    ) => {
        const { currentPage, pageSize } = this.state;

        this.setState({ processEfficiencyLoading: true });

        const handleProcessEfficiencyResponse = (response: any) => {
            const processEfficiencyRows = (
                response.data.data as Array<ProcessEfficiencyRow>
            )
                .map(ProcessEfficiencyRowFromJSON)
                .map((item) => {
                    // lead_time calculation: If "close_date" is empty, the current date is used, otherwise "close_date" is used.
                    const closeDate = item.close_date
                        ? item.close_date
                        : new Date();
                    const startDate = item.start_date;
                    const startDateOnly = new Date(
                        startDate.getUTCFullYear(),
                        startDate.getUTCMonth(),
                        startDate.getUTCDate(),
                    );
                    const closeDateOnly = new Date(
                        closeDate.getUTCFullYear(),
                        closeDate.getUTCMonth(),
                        closeDate.getUTCDate(),
                    );

                    const daysDifference =
                        (closeDateOnly.getTime() - startDateOnly.getTime()) /
                        (1000 * 60 * 60 * 24);
                    const leadTime = daysDifference * 8;

                    // Process efficiency calculation: total_spent_time / lead_time
                    const processEfficiency =
                        leadTime > 0 ? (item.total_spent_time / leadTime) * 100 : 0;

                    // Effort deviation calculation: total_spent_time - estimated_time
                    const effortDeviation =
                        item.total_spent_time -
                        (item.estimated_time ? item.estimated_time : 0);

                    // Deviation rate calculation:
                    // If (total_spent_time - estimated_time) < 0, set to 0.
                    // Otherwise, calculate (total_spent_time - estimated_time) / estimated_time.
                    // If estimated_time is not available, set to null.
                    const deviationRate = item.estimated_time
                        ? effortDeviation < 0
                            ? 0
                            : (effortDeviation / item.estimated_time) * 100
                        : null;

                    return {
                        ...item,
                        days_difference: daysDifference,
                        process_efficiency: processEfficiency,
                        effort_deviation: effortDeviation,
                        deviation_rate: deviationRate,
                    };
                });

            this.setState({
                processEfficiencyRows,
                totalItems: response.data.total_items,
                processEfficiencyLoading: false,
            });
        };

        const queryParams = {
            page: currentPage,
            per_page: pageSize,
            closed_on: filters.closed_on,
            assignee: filters.assignee,
            development_stage: filters.development_stage,
        };

        BitmakerAPIClient.get(
            `/professional_services/process_efficiency`,
            queryParams,
            handleProcessEfficiencyResponse,
        );
    };

    fetchDevelopersListData = () => {
        const handleDevelopersResponse = (response: any) => {
            this.setState({
                developersList: response.data.data,
            });
        };

        BitmakerAPIClient.get(
            `/professional_services/developers`,
            {},
            handleDevelopersResponse,
        );
    };

    fetchDevelopmentStagesListData = () => {
        const handleDevelopmentStagesResponse = (response: any) => {
            this.setState({
                developmentStagesList: response.data.data,
            });
        };

        BitmakerAPIClient.get(
            `/professional_services/development_stages`,
            {},
            handleDevelopmentStagesResponse,
        );
    };

    fetchProcessEfficiencyOverTimeData = () => {
        this.setState({ processEfficiencyOverTimeLoading: true });

        const handleProcessEfficiencyOverTimeResponse = (response: any) => {
            this.setState({
                processEfficiencyOverTimeRows: response.data.data,
                processEfficiencyOverTimeLoading: false,
            });
        };

        BitmakerAPIClient.get(
            `/professional_services/process_efficiency_over_time`,
            {},
            handleProcessEfficiencyOverTimeResponse,
        );
    };

    fetchMetricsPerDeveloperData = () => {
        const { contractCountTableSelectedFilter } = this.state;

        this.setState({ metricsPerDeveloperLoading: true });

        const handleContractCountPerUserResponse = (response: any) => {
            this.setState({
                metricsPerDeveloperRows: response.data.data,
                metricsPerDeveloperLoading: false,
            });
        };

        BitmakerAPIClient.get(
            `/professional_services/metrics_per_developer`,
            { status: contractCountTableSelectedFilter },
            handleContractCountPerUserResponse,
        );
    };

    fetchContractCountPerStageData = () => {
        this.setState({ contractCountPerStageLoading: true });

        const handleContractCountPerStageResponse = (response: any) => {
            this.setState({
                contractCountPerStageRows: response.data.data,
                contractCountPerStageLoading: false,
            });
        };

        BitmakerAPIClient.get(
            `/professional_services/contract_count_per_stage`,
            {},
            handleContractCountPerStageResponse,
        );
    };

    fetchWorkloadData = () => {
        this.setState({ workloadLoading: true });

        const handleWorkloadDataReponse = (response: any) => {
            const currentDate = new Date();

            const updatedRows = response.data.data.map((row: any) => {
                const dateRangeRegex =
                    /\((\d{2}\/\d{2}\/\d{4}) - (\d{2}\/\d{2}\/\d{4})\)/;
                const match = row.sprint_name.match(dateRangeRegex);

                if (match) {
                    const [, startDateStr, endDateStr] = match;

                    const startDate = new Date(
                        startDateStr.split("/").reverse().join("-") + "T00:00:00Z",
                    );
                    const endDate = new Date(
                        endDateStr.split("/").reverse().join("-") + "T23:59:59Z",
                    );

                    const isCurrent =
                        currentDate >= startDate && currentDate <= endDate;

                    return { ...row, is_current: isCurrent };
                }

                return { ...row, is_current: false };
            });

            this.setState({
                workloadRows: updatedRows,
                workloadLoading: false,
            });
        };

        BitmakerAPIClient.get(
            `/professional_services/workload`,
            {},
            handleWorkloadDataReponse,
        );
    };

    fetchMaintenanceEpicsData = () => {
        this.setState({ maintenanceEpicsLoading: true });

        const handleMaintenanceEpicsResponse = (response: any) => {
            this.setState({
                maintenanceEpicsRows: (
                    response.data.data as Array<MaintenanceEpicRow>
                ).map(MaintenanceEpicRowFromJSON),
                maintenanceEpicsLoading: false,
            });
        };

        BitmakerAPIClient.get(
            `/professional_services/maintenance_epics`,
            {},
            handleMaintenanceEpicsResponse,
        );
    };

    handleProcessEfficiencyTableChange = (
        pagination: TablePaginationConfig,
        filters: Record<string, (string | number | boolean)[] | null>,
    ) => {
        const closedOnValue = filters.close_date ? filters.close_date[0] : null;

        const assigneeValues = filters.assignee || [];
        const assigneeIds = assigneeValues
            .filter((value) => typeof value === "number")
            .map((value) => Number(value));

        const developmentStageValues = filters.development_stage || [];
        const developmentStageNames = developmentStageValues
            .filter((value) => typeof value === "string")
            .map((value) => String(value));

        const filterParams = {
            closed_on: typeof closedOnValue === "string" ? closedOnValue : null,
            assignee: assigneeIds.length > 0 ? assigneeIds : null,
            development_stage:
                developmentStageNames.length > 0 ? developmentStageNames : null,
        };

        this.setState(
            {
                currentPage: pagination.current || 1,
                pageSize: pagination.pageSize || 10,
            },
            () => {
                this.fetchProcessEfficiencyData(filterParams);
            },
        );
    };

    handleContractCountTableFilterChange = (e: RadioChangeEvent) => {
        const contractCountTableSelectedFilter = e.target.value.toString();

        this.setState({ contractCountTableSelectedFilter }, () => {
            this.fetchMetricsPerDeveloperData();
        });
    };

    renderContractCountPerStageBarChart = () => {
        const { contractCountPerStageRows } = this.state;

        return (
            <ResponsiveContainer width="100%" height={400}>
                <BarChart
                    data={contractCountPerStageRows}
                    margin={{ top: 20, right: 20, left: 0, bottom: 20 }}
                >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                        dataKey="stage_name"
                        interval={0}
                        tick={({ x, y, payload }) => {
                            const stageValue = payload.value
                                ? String(payload.value)
                                : "";
                            const words = stageValue.split(" ");
                            return (
                                <g transform={`translate(${x},${y + 10})`}>
                                    {words.map((word: string, index: number) => (
                                        <text
                                            key={index}
                                            x={0}
                                            y={index * 14}
                                            textAnchor="middle"
                                            fill="rgb(102, 102, 102)"
                                            className="text-[7px] sm:text-[8px] md:text-[9px] lg:text-xs"
                                        >
                                            {word}
                                        </text>
                                    ))}
                                </g>
                            );
                        }}
                    />
                    <YAxis
                        domain={[
                            0,
                            Math.max(
                                ...contractCountPerStageRows.map(
                                    (data) => data.contract_count,
                                ),
                            ) + 1,
                        ]}
                        tick={({ x, y, payload }) => (
                            <g transform={`translate(${x - 10},${y})`}>
                                <text
                                    dy={3}
                                    textAnchor="end"
                                    fill="rgb(102, 102, 102)"
                                    className="text-[7px] sm:text-[8px] md:text-[9px] lg:text-xs"
                                >
                                    {payload.value}
                                </text>
                            </g>
                        )}
                    />
                    <RechartsTooltip
                        content={({ active, payload, label }) => {
                            if (active && payload && payload.length) {
                                return (
                                    <div className="bg-white p-2 rounded shadow-lg text-[rgba(0,0,0,0.88)]">
                                        <p className="font-bold">{`${label}`}</p>
                                        <p>{`Contract count: ${payload[0].value}`}</p>
                                    </div>
                                );
                            }
                            return null;
                        }}
                    />
                    <Bar
                        dataKey="contract_count"
                        barSize={50}
                        label={{
                            position: "top",
                            fill: "rgb(102, 102, 102)",
                            className:
                                "text-[9px] sm:text-[10px] md:text-[11px] lg:text-xs",
                        }}
                    >
                        {contractCountPerStageRows.map((entry, index) => (
                            <Cell
                                key={`cell-${index}`}
                                fill={stageColors[entry.stage_name] || "#8884d8"}
                            />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        );
    };

    renderProcessEfficiencyLegend() {
        return (
            <div className="flex flex-wrap mt-8 mb-4 ml-3">
                {Object.entries(stageColors).map(([stage, color]) => (
                    <div key={stage} className="flex items-center mr-4 mb-2">
                        <span
                            className="w-4 h-4 md:w-5 md:h-5 inline-block mr-2 rounded-sm"
                            style={{ backgroundColor: color as string }}
                        ></span>
                        <span className="text-[rgba(0,0,0,0.88)]">{stage}</span>
                    </div>
                ))}
            </div>
        );
    };

    public render() {
        const {
            processEfficiencyRows,
            processEfficiencyOverTimeRows,
            currentPage,
            pageSize,
            totalItems,
            processEfficiencyLoading,
            metricsPerDeveloperLoading,
            processEfficiencyOverTimeLoading,
            workloadLoading,
            maintenanceEpicsLoading,
            metricsPerDeveloperRows,
            workloadRows,
            developersList,
            developmentStagesList,
            contractCountTableSelectedFilter,
            maintenanceEpicsRows,
        } = this.state;

        return (
            <Layout>
                <Content>
                    {/* Process Efficiency */}
                    <div className="table-section">
                        <div className="table-title">
                            <Title level={5}>Process Efficiency</Title>
                        </div>

                        {/* Legend of colors */}
                        {this.renderProcessEfficiencyLegend()}

                        <div className="table-scroll">
                            <Table
                                columns={this.processEfficiencyColumns.map((column) => {
                                    if (column.key === "assignee") {
                                        return {
                                            ...column,
                                            filters: developersList.map((dev) => ({
                                                text: dev.login,
                                                value: dev.id,
                                            })),
                                        };
                                    } else if (column.key === "lead_time") {
                                        const selectedUnit = this.state.selectedLeadTimeUnit;
                                        return {
                                            ...column,
                                            title: (
                                                <Tooltip
                                                    title={`
                                                        Total time from the start to the close of the epic.
                                                        If closed, it's (Close date - Start date).
                                                        If still open, it's (today's date - Start date).
                                                        The time is displayed in calendar days or hours, depending on the selected unit.
                                                    `}
                                                    placement="leftBottom"
                                                >
                                                    Lead time
                                                    <br />
                                                    <i style={{ fontSize: "0.8em" }}>
                                                        Total time active ({selectedUnit})
                                                    </i>
                                                    <br />
                                                    <Switch
                                                        checked={selectedUnit === "hours"}
                                                        onChange={(checked) => {
                                                            const newUnit = checked
                                                                ? "hours"
                                                                : "days";
                                                            this.setState({
                                                                selectedLeadTimeUnit: newUnit,
                                                            });
                                                        }}
                                                        checkedChildren="Hours"
                                                        unCheckedChildren="Days"
                                                        style={{ marginTop: 8 }}
                                                    />
                                                </Tooltip>
                                            ),
                                        };
                                    } else if (column.key === "development_stage") {
                                        return {
                                            ...column,
                                            filters: developmentStagesList.map((stage) => ({
                                                text: stage.name,
                                                value: stage.name,
                                            })),
                                        };
                                    };
                                    return column;
                                })}
                                dataSource={processEfficiencyRows}
                                size={"middle"}
                                pagination={{
                                    current: currentPage,
                                    pageSize: pageSize,
                                    total: totalItems,
                                    showSizeChanger: true,
                                }}
                                loading={processEfficiencyLoading}
                                onChange={this.handleProcessEfficiencyTableChange}
                                bordered
                                rowKey={(record) => record.id}
                                rowClassName={(record) => {
                                    switch (record.development_stage) {
                                        case "Analysis":
                                            return "row-analysis";
                                        case "Development":
                                            return "row-development";
                                        case "Quality Assurance":
                                            return "row-quality-assurance";
                                        case "Data Sample Approval":
                                            return "row-data-sample-approval";
                                        case "Full Crawl Approval":
                                            return "row-full-crawl-approval";
                                        case "Project Handover":
                                            return "row-project-handover";
                                        case "Support":
                                            return "row-support";
                                        default:
                                            return "";
                                    }
                                }}
                            />
                        </div>
                    </div>

                    {/* Process Efficiency for last 30, 90, 180 days */}
                    <div className="table-section">
                        <div className="table-title">
                            <Title level={5}>Process Efficiency Over Time</Title>
                        </div>

                        <div className="table-scroll">
                            <Table
                                columns={[
                                    {
                                        title: "Period",
                                        dataIndex: "period",
                                        key: "period",
                                        width: "25%",
                                    },
                                    {
                                        title: "Last 30 Days",
                                        dataIndex: "last_30_days",
                                        key: "last_30_days",
                                        width: "25%",
                                        render: (text) => `${text}%`,
                                    },
                                    {
                                        title: "Last 90 Days",
                                        dataIndex: "last_90_days",
                                        key: "last_90_days",
                                        width: "25%",
                                        render: (text) => `${text}%`,
                                    },
                                    {
                                        title: "Last 180 Days",
                                        dataIndex: "last_180_days",
                                        key: "last_180_days",
                                        width: "25%",
                                        render: (text) => `${text}%`,
                                    },
                                ]}
                                dataSource={[
                                    {
                                        key: "1",
                                        period: "Process Efficiency",
                                        last_30_days:
                                            processEfficiencyOverTimeRows
                                                .find((item) => item.period === "30_days")
                                                ?.process_efficiency?.toFixed(2) || 0,
                                        last_90_days:
                                            processEfficiencyOverTimeRows
                                                .find((item) => item.period === "90_days")
                                                ?.process_efficiency?.toFixed(2) || 0,
                                        last_180_days:
                                            processEfficiencyOverTimeRows
                                                .find((item) => item.period === "180_days")
                                                ?.process_efficiency?.toFixed(2) || 0,
                                    },
                                ]}
                                size={"small"}
                                pagination={false}
                                loading={processEfficiencyOverTimeLoading}
                                bordered
                            />
                        </div>
                    </div>

                    <Row>
                        {/* Developers */}
                        <Col xs={24} sm={24} md={10} lg={10}>
                            <div className="table-section">
                                <div className="table-title">
                                    <Title level={5}>Developers</Title>
                                </div>
                                <div style={{ marginTop: 18, marginBottom: 10, marginLeft: 13 }}>
                                    <strong className="text-[rgba(0,0,0,0.88)]">Contracts: </strong>
                                    <Radio.Group
                                        options={[
                                            { label: "All", value: "all" },
                                            { label: "Open", value: "open" },
                                            { label: "Closed", value: "closed" },
                                        ]}
                                        value={contractCountTableSelectedFilter}
                                        onChange={this.handleContractCountTableFilterChange}
                                        style={{ marginLeft: 10 }}
                                    />
                                </div>
                                <div className="table-scroll">
                                    <Table
                                        columns={this.metricsPerDeveloperColumns}
                                        dataSource={metricsPerDeveloperRows}
                                        size={"small"}
                                        pagination={false}
                                        loading={metricsPerDeveloperLoading}
                                        bordered
                                        rowKey={(record) => record.user_login}
                                    />
                                </div>
                            </div>
                        </Col>

                        {/* Contract Count per Development Stage Bar Chart */}
                        <Col xs={24} sm={24} md={14} lg={14}>
                            <div className="table-section">
                                <div className="table-title">
                                    <Title level={5}>Contract Count per Development Stage</Title>
                                </div>
                                <div className="chart-section">
                                    {this.renderContractCountPerStageBarChart()}
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Workload */}
                    <div className="table-section">
                        <div className="table-title">
                            <Title level={5}>Workload</Title>
                        </div>
                        <div className="table-scroll">
                            <Table
                                columns={this.workloadColumns}
                                dataSource={[...workloadRows].reverse()}
                                size={"small"}
                                pagination={false}
                                loading={workloadLoading}
                                bordered
                                rowKey={(record) => record.sprint_id}
                                rowClassName={(record) =>
                                    record.is_current ? "current-sprint-row" : ""
                                }
                            />
                        </div>
                    </div>

                    {/* Maintenance Epics */}
                    <div className="table-section">
                        <div className="table-title">
                            <Title level={5}>Maintenance Epics</Title>
                        </div>
                        <div className="table-scroll">
                            <Table
                                columns={this.maintenanceEpicsColumns}
                                dataSource={maintenanceEpicsRows}
                                size={"middle"}
                                pagination={false}
                                loading={maintenanceEpicsLoading}
                                bordered
                                rowKey={record => record.project_id}
                            />
                        </div>
                    </div>
                </Content>
            </Layout>
        );
    };
};
